// Helper functions for creating TanStack Table columns

/**
 * Create a simple text column
 */
export const createTextColumn = (accessorKey, header, size = 150) => ({
  id: accessorKey,
  accessorKey,
  header,
  size,
});

/**
 * Create an action column with view/edit/delete buttons or custom render function
 * Supports two usage patterns:
 * 1. createActionColumn({ onView, onEdit, onDelete, size = 100, roleKey, viewTooltip, editTooltip, deleteTooltip }) - with callbacks
 * 2. createActionColumn("id", "Action", renderFunction, size) - with custom renderer
 */
export const createActionColumn = (firstParam, header, renderFunction, size = 100) => {
  // Check if first parameter is an object (callback style) or string (custom render style)
  if (typeof firstParam === 'object' && firstParam !== null) {
    // Callback style: { onView, onEdit, onDelete, size, roleKey, viewTooltip, editTooltip, deleteTooltip }
    const {
      onView,
      onEdit,
      onDelete,
      size: callbackSize = 100,
      roleKey,
      viewTooltip = 'View',
      editTooltip = 'Edit',
      deleteTooltip = 'Delete',
      canDelete = (rKey) => true,
      canView = () => true,
      canEdit = () => true,
    } = firstParam;
    return {
      id: 'actions',
      header: 'Action',
      size: callbackSize,
      enableSorting: false,
      cell: ({ row }) => {
        const rKey = roleKey;
        return (
          <div>
            <ul className="list-inline mb-0">
              {onView && canView(rKey, row.original) && (
                <li className="list-inline-item mt-n1">
                  <button 
                    onClick={() => onView(row.original)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    aria-label="View"
                    type="button"
                    className="me-2"
                  >
                    <i className="uil uil-eye text-warning action-btn font-size-18"></i>
                  </button>
                </li>
              )}
              {onEdit && canEdit(rKey, row.original) && (
                <li className="list-inline-item mt-n1">
                  <button 
                    onClick={() => onEdit(row.original)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    aria-label="Edit"
                    type="button"
                    className="me-2"
                  >
                    <i className="uil uil-pen font-size-18"></i>
                  </button>
                </li>
              )}
              {onDelete && canDelete(rKey, row.original) && (
                <li className="list-inline-item mt-n1">
                  <button 
                    onClick={() => onDelete(row.original)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    aria-label="Delete"
                    type="button"
                  >
                    <i className="uil uil-trash-alt font-size-18 text-danger"></i>
                  </button>
                </li>
              )}
            </ul>
          </div>
        );
      },
    };
  } else {
    // Custom render style: createActionColumn("id", "Action", renderFunction, size)
    return {
      id: firstParam || 'actions',
      header: header || 'Action',
      size,
      enableSorting: false,
      cell: ({ row }) => renderFunction(row.original),
    };
  }
};

/**
 * Create a custom formatted column
 */
export const createFormattedColumn = (accessorKey, header, formatter, size = 150) => ({
  id: accessorKey,
  accessorKey,
  header,
  size,
  cell: ({ row }) => formatter(row.original),
});

/**
 * Create a link column
 */
export const createLinkColumn = (accessorKey, header, onClick, size = 150) => ({
  id: accessorKey,
  accessorKey,
  header,
  size,
  cell: ({ row, getValue }) => (
    <button
      onClick={() => onClick(row.original)}
      style={{ 
        background: 'none', 
        border: 'none', 
        color: '#007bff', 
        cursor: 'pointer',
        textDecoration: 'underline',
        padding: 0
      }}
      type="button"
    >
      {getValue()}
    </button>
  ),
});
